import { useCallback, useEffect, useState } from 'react';
import ActivitySparkline from '../components/charts/ActivitySparkline';
import BarRows from '../components/charts/BarRows';
import Dropdown from '../components/Dropdown';
import Icon from '../components/Icon';
import { ErrorBanner, InfoBanner, Toast } from '../components/Feedback';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

const TABS = [
  { key: 'usage', label: 'Usage analytics' },
  { key: 'activation', label: 'Activation' },
];

/** Ranges people actually ask for, rather than making them type two dates every time. */
const PRESETS = [
  { key: '7', label: 'Last 7 days', days: 7 },
  { key: '30', label: 'Last 30 days', days: 30 },
  { key: '90', label: 'Last 90 days', days: 90 },
];

/**
 * Module 12's two reports, with the date-range and dimension filters the guideline
 * specifies and CSV/PDF export.
 *
 * <p>The filters are sent to the server rather than applied here: a Sales Rep's report
 * is narrowed in the database, so what arrives is already only theirs, and the export
 * cannot return rows the screen would have hidden.
 */
export default function ReportsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('usage');
  const [filters, setFilters] = useState(() => rangeOf(30));
  const [schools, setSchools] = useState([]);
  const [titles, setTitles] = useState([]);
  const [usage, setUsage] = useState(null);
  const [activation, setActivation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // The pickers offer only what this caller may ask for; the server refuses anything
  // else, so the two agree rather than one silently allowing more.
  useEffect(() => {
    api.listSchools({ size: 100 }).then((page) => setSchools(page.items)).catch(() => setSchools([]));
    api.listTitles().then(setTitles).catch(() => setTitles([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = queryOf(filters);
      const [usageReport, activationReport] = await Promise.all([
        api.usageReport(params),
        api.activationReport(params),
      ]);
      setUsage(usageReport);
      setActivation(activationReport);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  async function exportAs(format) {
    setExporting(true);
    setError(null);
    try {
      const rows = await api.exportReport(tab, queryOf(filters), format);
      setToast(`${rows || ''} ${format} export downloaded`.trim());
    } catch (err) {
      setError(err);
    } finally {
      setExporting(false);
    }
  }

  function set(name, value) {
    setFilters((current) => ({ ...current, [name]: value }));
  }

  const report = tab === 'usage' ? usage : activation;

  return (
    <>
      <div className="page-head row-between">
        <div>
          <h1>Reports</h1>
          <p>
            {user?.role === 'SALES_REP'
              ? 'Usage and activation across the schools in your states.'
              : 'Usage and activation across the platform.'}
          </p>
        </div>
        <div className="toolbar">
          <button type="button" className="btn btn-secondary" disabled={exporting}
                  onClick={() => exportAs('CSV')}>
            <Icon name="download" size={16} /> CSV
          </button>
          <button type="button" className="btn btn-secondary" disabled={exporting}
                  onClick={() => exportAs('PDF')}>
            <Icon name="download" size={16} /> PDF
          </button>
        </div>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      <div className="filter-chips" style={{ marginBottom: 'var(--s1)' }} role="tablist">
        {TABS.map((entry) => (
          <button
            type="button"
            key={entry.key}
            role="tab"
            className="chip"
            aria-pressed={tab === entry.key}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <section className="card card-pad" style={{ marginBottom: 'var(--s1)' }}>
        <div className="toolbar">
          {PRESETS.map((preset) => (
            <button
              type="button"
              key={preset.key}
              className="chip"
              aria-pressed={isRange(filters, preset.days)}
              onClick={() => setFilters(rangeOf(preset.days))}
            >
              {preset.label}
            </button>
          ))}

          <input
            type="date"
            className="input"
            style={{ flex: '0 1 150px' }}
            aria-label="From date"
            value={filters.from}
            max={filters.to}
            onChange={(event) => set('from', event.target.value)}
          />
          <input
            type="date"
            className="input"
            style={{ flex: '0 1 150px' }}
            aria-label="To date"
            value={filters.to}
            min={filters.from}
            onChange={(event) => set('to', event.target.value)}
          />

          <div style={{ flex: '0 1 190px' }}>
            <Dropdown
              ariaLabel="Filter by school"
              options={schools}
              value={filters.schoolId}
              onChange={(schoolId) => set('schoolId', schoolId)}
              placeholder="All schools"
            />
          </div>

          <div style={{ flex: '0 1 190px' }}>
            <Dropdown
              ariaLabel="Filter by title"
              options={titles}
              value={filters.titleId}
              onChange={(titleId) => set('titleId', titleId)}
              placeholder="All titles"
            />
          </div>
        </div>
      </section>

      {loading && <div className="empty">Loading…</div>}

      {!loading && report?.scoped && (
        <InfoBanner>
          This report covers the schools in the states assigned to you. Platform-wide figures are the
          Super Admin&apos;s.
        </InfoBanner>
      )}

      {!loading && tab === 'usage' && usage && <UsagePanels report={usage} />}
      {!loading && tab === 'activation' && activation && <ActivationPanels report={activation} />}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2400)} />
    </>
  );
}

function UsagePanels({ report }) {
  return (
    <>
      <div className="kpi-row">
        <Kpi label="Resources opened" value={report.totalOpens}
             note={`${report.views.toLocaleString()} viewed · ${report.streams.toLocaleString()} streamed`} />
        <Kpi label="People" value={report.distinctUsers} note="Opened at least one resource" />
        <Kpi label="Signed in" value={report.activeUsers} note="Accounts active in this period" />
        <Kpi label="Downloads" value={report.downloads} note="Teacher downloads only" />
      </div>

      <div className="panel-grid">
        <section className="card card-pad panel" style={{ gridColumn: '1 / -1' }}>
          <h2>Opens per day</h2>
          <p className="panel-sub">
            {report.from} to {report.to}
            {report.schoolName ? ` · ${report.schoolName}` : ''}
            {report.titleName ? ` · ${report.titleName}` : ''}
          </p>
          <ActivitySparkline days={report.perDay.map((day) => ({ date: day.date, opens: day.opens }))} />
        </section>

        <section className="card card-pad panel">
          <h2>By title</h2>
          <p className="panel-sub">Which books are actually being opened.</p>
          <BarRows slices={report.byTitle} emptyMessage="Nothing was opened in this period." />
        </section>

        <section className="card card-pad panel">
          <h2>By school</h2>
          <p className="panel-sub">Teachers and students counted together.</p>
          <BarRows slices={report.bySchool} emptyMessage="Nothing was opened in this period." />
        </section>
      </div>

      <p className="tiny muted" style={{ marginTop: 'var(--s2)' }}>
        Sign-ins are counted as people active in the period, not as individual visits —
        nothing records each sign-in separately. Dates are UTC.
      </p>
    </>
  );
}

function ActivationPanels({ report }) {
  return (
    <>
      <div className="kpi-row">
        {!report.scoped && (
          <Kpi label="Generated" value={report.generated} note="Minted in this period" />
        )}
        <Kpi label="Allocated" value={report.allocated} note="Handed to schools in this period" />
        <Kpi label="Redeemed" value={report.used} note="Activated by students in this period" />
        <Kpi
          label="Redemption"
          value={report.allocated === 0 ? 0 : Math.round((report.used / report.allocated) * 100)}
          suffix="%"
          note="Of what was allocated in this period"
        />
      </div>

      <div className="panel-grid">
        {!report.scoped && report.generatedByTitle.length > 0 && (
          <section className="card card-pad panel">
            <h2>Generated by title</h2>
            <p className="panel-sub">Codes have no school until they are allocated.</p>
            <BarRows slices={report.generatedByTitle} />
          </section>
        )}

        <section className="card card-pad panel" style={{ gridColumn: '1 / -1' }}>
          <h2>By school and title</h2>
          <p className="panel-sub">
            {report.from} to {report.to} · {report.rows.length} row
            {report.rows.length === 1 ? '' : 's'}
          </p>
          {report.rows.length === 0 ? (
            <p className="muted small" style={{ margin: 0 }}>
              No codes were allocated or redeemed in this period.
            </p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>School</th>
                    <th>Title</th>
                    <th>Allocated</th>
                    <th>Redeemed</th>
                    <th>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((row) => (
                    <tr key={`${row.schoolName}/${row.titleName}`}>
                      <td>{row.schoolName}</td>
                      <td>{row.titleName}</td>
                      <td className="mono">{row.allocated.toLocaleString()}</td>
                      <td className="mono">{row.used.toLocaleString()}</td>
                      <td className="mono">
                        {row.allocated === 0 ? '—' : `${Math.round((row.used / row.allocated) * 100)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function Kpi({ label, value, note, suffix }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">
        {value.toLocaleString()}
        {suffix && <span style={{ fontSize: 'var(--fs-lg)' }}>{suffix}</span>}
      </div>
      {note && <div className="kpi-note">{note}</div>}
    </div>
  );
}

function rangeOf(days) {
  const to = new Date();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { from: iso(from), to: iso(to), schoolId: '', titleId: '' };
}

function isRange(filters, days) {
  const candidate = rangeOf(days);
  return filters.from === candidate.from && filters.to === candidate.to;
}

function queryOf(filters) {
  return {
    from: filters.from,
    to: filters.to,
    schoolId: filters.schoolId || undefined,
    titleId: filters.titleId || undefined,
  };
}

function iso(date) {
  return date.toISOString().slice(0, 10);
}
